import { SHAPE_TEXT_BOLD, SHAPE_TEXT_UNDERLINE, SHAPE_TEXT_ITALIC } from '../shape-widget-properties'

import _ from 'lodash'

import ImpactShape from 'impact-shape'
import { adjustLightness, lookup } from 'scoreboard-colors'
import { GRAY, GRAY_MEDIUM, SLATE } from 'scoreboard/components/colors'

var draw = function (widgetView, enter, update, newData) {
	let trendSvgEnter = update.selectAll('svg.trend')
		.data([newData]).enter()
		.append('svg')
			.attr('class', 'trend')
			// We aren't actually showing this SVG; instead, we pass it to
			// ImpactShape to use as a badge
			.style('display', 'none')

	// Exports have a bug with an unknown cause that makes random SVGs get
	// mispositioned without absolute positioning. To do that, the SVG needs to
	// be wrapped in a div of the same height so the content after it doesn't
	// get misaligned from its absolute position. Issues #20413 #20408
	update.select('.shape-wrapper')
		.attr('style', (d) => `height: ${d.shapeHeight}px`)

	let trendSvgId = `trend-${newData.cid}`

	let symbolEnter = trendSvgEnter
		.append('symbol')
		.attr('viewBox', '0 0 100 100')
		.attr('id', trendSvgId)

	var trendSvgUpdate = update.select('svg.trend')

	drawTrend(symbolEnter, trendSvgUpdate)

	let shapeOptions = buildShapeOptions({ newData, trendSvgId })

	let dimensions = {
		width: newData.shapeWidth,
		height: newData.shapeHeight,
		rotate: widgetView.model.wob.get('rotate')
	}

	// Harmless to have this appear in the console, and really helpful for
	// debugging shape issues right now
	console.log(dimensions, shapeOptions)

	if (widgetView.shape) {
		// If the shape dimensions have changed, we don't want to transition.
		// For example, when resizing, the shape transition can lag behind the
		// user's mouse movement. Or when the background is turned on, Impact
		// adjusts the widget immediately for a smaller shape, so transitioning
		// the shape dimensions would look weird.
		let transition = _.isEqual(dimensions, widgetView.currentShapeDimensions)
		widgetView.shape.render(dimensions, shapeOptions, transition)
	} else {
		createImpactShape({ widgetView, dimensions, shapeOptions })
	}

	widgetView.currentShapeOptions = shapeOptions
	widgetView.currentShapeDimensions = dimensions
}

function buildShapeOptions ({ newData, trendSvgId }) {
	// #57454 impact-shape uses the term 'gradient', but Impact has used 'linear'
	// since at least v2. To avoid an upgrade task, complicating existing
	// tasks that involve the 'linear' fill, and tons of other changes, we'll
	// just adjust before passing it to impact-shape
	let fill = newData.properties.fill === 'linear'
		? 'gradient' : newData.properties.fill

	let shapeOptions = {
		symbol: newData.domainData?.symbol,
		fill,
		color: newData.domainData?.color,
		strokeWidth: newData.domainData?.strokeWidth,
		stroke: newData.domainData?.stroke,
		handles: newData.domainData?.handles,
		tipSize: newData.domainData?.tipSize,
		opacity: newData.domainData?.opacity,
		hoverText: newData.domainData?.hoverText
	}

	if (!_.isNil(newData.domainData?.shapeText)) {
		let styles = [SHAPE_TEXT_BOLD, SHAPE_TEXT_ITALIC, SHAPE_TEXT_UNDERLINE]
			.filter(p => {
				return newData.properties[p]
			})
			.map(p => textStylePropertyToApi[p])

		shapeOptions.text = {
			value: newData.domainData?.shapeText,
			truncatedValue: newData.domainData?.abbreviatedShapeText,
			color: newData.domainData?.shapeTextColor,
			fontSize: newData.properties.fontSize,
			styles,
			align: newData.properties.textAlign,
			rotation: newData.domainData?.shapeTextRotation
		}
	}

	if (newData.properties.showTrend) {
		shapeOptions.badges = [ trendSvgId ]
	}

	if (newData.isPlaceholder) {
		// #61257 for placeholders, override some configured styles
		shapeOptions.color = GRAY_MEDIUM
		shapeOptions.opacity = .17
	}

	return shapeOptions
}

function createImpactShape ({ widgetView, dimensions, shapeOptions }) {
	widgetView.shape = new ImpactShape(widgetView.$('.svg-container')[0])

	let firstRender = true
	widgetView.shape.on('change:data', changes => {
		widgetView.onShapeChangeData({ changes, firstRender })
	})

	widgetView.shape.on('handle:dragend', changes => {
		widgetView.onHandleDragEnd(changes)
	})

	widgetView.shape.render(dimensions, shapeOptions)
	firstRender = false
}

function drawTrend (symbolEnter, svg) {
	symbolEnter.append('circle')
		.classed('trend-circle', true)

	var circle = svg.select('.trend-circle')
		.classed('is-not-displayed', function (d) {
			return !d.properties.showTrend
		})
		// #56606 need to use inline styles instead of CSS for Firefox to work
		.style('fill', lookup('white').rgbaString)
		.style('opacity', 0.75)

	circle
		// impact-shape takes care of sizing and positioning, so just make this
		// trend shape make sense in a 100x100 box
		.attr('cx', 50)
		.attr('cy', 50)
		.attr('r', 50)

	symbolEnter.append('use')
		.classed('trend-arrow', true)

	var arrow = svg.select('.trend-arrow')
		.attr('xlink:href', function (d) {
			var dir = _.get(d, 'domainData.trend.direction')
			if (dir === 'up' || dir === 'down') {
				// Arrow points up. If we need it to point down,
				// we'll flip it with a transform
				return '#icon-arrow'
			} else if (dir === 'level') {
				return '#icon-arrow-sides'
			} else {
				return '#icon-trend-none'
			}
		})
		.attr('transform', function (d) {
			var dir = _.get(d, 'domainData.trend.direction')
			if (dir == 'down') {
				// The arrow points up, so flip it. Note that we can't use CSS transform
				// because of IE (up to at least 11)
				// https://connect.microsoft.com/IE/feedback/details/811744/ie11-bug-with-implementation-of-css-transforms-in-svg

				return 'rotate(180 50 50)'
			}

			return 'translate(0,0) rotate(0)'
		})
		.style('fill', d => {
			let color = d.domainData.trend?.color ?? SLATE
			if (color === GRAY) {
				// #24700 if showing the gray value, use slate instead
				color = SLATE
			}

			return adjustLightness(color, -10)
		})

	arrow
		// impact-shape takes care of sizing and positioning, so just make this
		// trend shape make sense in a 100x100 box
		.attr('height', 70)
		.attr('width', 70)
		.attr('x', 15)
		.attr('y', 15)
}

// ensure number is at least 0
var min0 = Math.max.bind(null, 0)

var square = _.partialRight(Math.pow, 2)

function circleCoord (d) {
	if (d.properties.shape === 'ellipse') {
		// The equation for plotting points for an ellipse is:
		// (x^2/a^2) + (y^2/b^2) = 1
		// This code basically uses that equation, though it has to do some
		// shifting because 0,0 in SVGs is the top-left corner

		var a = d.shapeWidth / 2
		var b = d.shapeHeight / 2
		// We want x to be 1/4 of the way towards the center, but we
		// multiply by .75 to handle SVGs top-left coordinate system
		var x = a * .75

		// if the bubble has a 0 width or height, just set y to 0 or we'll get
		// a divide-by-zero error
		var y = (a === 0 || b === 0)
			? 0
			: Math.sqrt((1 - (square(x) / square(a))) * square(b))

		return {
			// Move it 1/5 of the way to the center so that it's a little
			// further out than right on the line.
			x: a / 5,
			y: b - y
		}
	} else {
		let minDimension = Math.min(d.shapeWidth, d.shapeHeight)
		// Bring it in 3% from the top-left corner
		return {
			x: minDimension * 0.03,
			y: minDimension * 0.03
		}
	}
}

function circleDim (d) {
	return min0(trendIndicatorSize(d) / 2)
}

function arrowDimensions (d) {
	// Make the arrow 75% the size of the circle
	return min0(trendIndicatorSize(d) * 0.75)
}

function arrowCoord (d) {
	// shift to allow for stroke/border
	var circle = circleCoord(d)
	var arrowSize = arrowDimensions(d)

	return {
		x: circle.x - (arrowSize/2),
		y: circle.y - (arrowSize/2)
	}
}

const baseBubbleWidth = 150
const baseBubbleHeight = 100
const baseTrendSize = 20
const maxTrendSize = 40
const minTrendSize = 15

function trendIndicatorSize (data) {
	// On the default 150 x 100 bubble, a 20 x 20 icon seems to look good.
	//
	// #21554 Scale the trend icon proportionally, using the dimension
	// that is smallest relative to the 150 x 100 default bubble size.
	// This avoids overly large trend icons on very wide/short or tall/narrow
	// bubbles.
	let scale = Math.min(data.shapeWidth / baseBubbleWidth, data.shapeHeight / baseBubbleHeight)
	let scaled = scale * baseTrendSize

	// But set some limits
	return Math.max(Math.min(scaled, maxTrendSize), minTrendSize)
}

function generatePath (x, y, width, height, rx, ry) {
	return `M ${x + rx}, ${y}` +
	`h ${width - 2 * rx}` +
	`a ${rx}, ${ry} 0 0 1 ${rx}, ${ry}` +
	`v ${height - 2 * ry}` +
	`a ${rx}, ${ry} 0 0 1 ${-rx}, ${ry}` +
	`h ${2 * rx - width}` +
	`a ${rx}, ${ry} 0 0 1 ${-rx}, ${-ry}` +
	`v ${2 * ry - height}` +
	`a ${rx}, ${ry} 0 0 1 ${rx}, ${-ry}` +
	`z`
}

function radius (shape, width, height) {
	switch (shape) {
		case 'rectangle': return {x: 0, y: 0}
		case 'rounded-rectangle':
			let scaledRadius = scale(width, height) * 10

			if (scaledRadius > (height / 2) || scaledRadius > (width / 2)) {
				// #20781 the scale() function attempts to keep the rounded corners
				// proportional to what they would be for a standard-sized bubble.
				// However, extreme differences between the width and height
				// (eg. very wide and very short) can't keep that same proportion.
				//
				// Thus, the radius cannot be less than the half the height or width.
				// Also subtract 1 from radius to allow for stroke/border
				scaledRadius = Math.min(width, height) / 2 - 1
			}

			return {
				x: scaledRadius,
				y: scaledRadius
			}
		case 'ellipse':
			return {
				// subtract 1 from radius to allow for stroke/border
				x: width / 2 - 1,
				y: min0(height / 2 - 1)
			}
	}
}

function drawShape (widgetView, shape, changingShape) {
	var x = 1
	var y = 1

	if (changingShape) {
		shape = shape.transition()
			.on('end', () => widgetView.trigger('transitioned:shape'))
	}

	shape
		.attr('d', function (d) {
			var width = min0(d.shapeWidth - 2) // subtract 1 for each side for stroke/border
			var height = min0(d.shapeHeight  - 2)
			var r = radius(d.properties.shape, width, height)
			return generatePath(x, y, width, height, r.x, r.y)
		})
		// #22140 title needs to be on the svg <path> for IE11 to use it for hover text
		.select('title').text(data => data.domainData.hoverText)
}

// Returns an appropriate scaling number for a wob's actual dimensions,
// based on the default of 200x120
function scale (width, height) {
	return ((width + height) / 2) / 120
}

function changing (oldData, newData, prop) {
	var oldVal = oldData && oldData.properties && oldData.properties[prop]
	var newVal = newData && newData.properties && newData.properties[prop]
	return _.isUndefined(oldVal) ? false : oldVal !== newVal
}

const textStylePropertyToApi = {
	[SHAPE_TEXT_BOLD]: 'bold',
	[SHAPE_TEXT_ITALIC]: 'italic',
	[SHAPE_TEXT_UNDERLINE]: 'underline'
}

export default draw

draw.__filename__ = 'dashboards/widgets/shape/view/draw-svg.js'
